import type {
  AuthTokens,
  LoginCredentials,
  ChangePasswordPayload,
  ForgotPasswordPayload,
  ResetPasswordPayload,
} from "./auth.types";
import type { AuthSession } from "./models/session.model";
import type { User } from "./models/user.model";
import { MOCK_DELAY_MS } from "./auth.constants";
import { isLegacyMockAuthAllowed } from "./auth-config";
import { mapSessionWithTokens } from "./auth.mapper";
import {
  clearStoredSession,
  isTokenExpired,
  loadStoredSession,
  saveSession,
  updateTokens,
} from "./auth.storage";
import { purgeAuthState } from "./purge-auth-state";
import { buildAuthTokens, isMockAccessToken, isValidRefreshToken } from "./auth.tokens";
import {
  hasStoredPassword,
  verifyStoredPassword,
} from "./data/mock-password.store";
import { rehydrateSessionUser, resolveAuthFromEmail } from "../identity/services/identity-auth.resolver";

/** Emails demo désactivés — session existante invalidée au rechargement. */
const BLOCKED_AUTH_EMAILS = new Set([
  "guest@itaas-rdc.com",
  "guess@itaas-rdc.com",
]);

function isBlockedAuthEmail(email: string): boolean {
  return BLOCKED_AUTH_EMAILS.has(email.trim().toLowerCase());
}

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export async function login(
  credentials: LoginCredentials
): Promise<AuthSession> {
  await delay(MOCK_DELAY_MS);

  if (!credentials.email || !credentials.password) {
    throw new Error("Email et mot de passe requis");
  }

  const email = credentials.email.trim().toLowerCase();
  const resolved = resolveAuthFromEmail(credentials.email);
  if (!resolved) {
    throw new Error("Identifiants invalides");
  }

  if (!hasStoredPassword(email)) {
    throw new Error("Identifiants invalides");
  }

  if (!verifyStoredPassword(email, credentials.password)) {
    throw new Error("Mot de passe incorrect");
  }

  const partial = {
    user: resolved.user,
    tenant: resolved.tenant,
    membership: resolved.membership,
  };
  const tokens = buildAuthTokens();
  const session = mapSessionWithTokens(partial, tokens);
  saveSession(session);

  const { userService } = await import("../identity/services/user.service");
  userService.recordLogin(session.user.id).catch(() => undefined);

  return session;
}

export async function logout(): Promise<void> {
  await delay(100);
  purgeAuthState();
}

export async function refreshToken(
  currentRefreshToken: string
): Promise<AuthTokens> {
  await delay(MOCK_DELAY_MS);

  if (!isValidRefreshToken(currentRefreshToken)) {
    throw new Error("Refresh token invalide");
  }

  const tokens = buildAuthTokens();
  updateTokens(tokens);
  return tokens;
}

export function getCurrentUser(): User | null {
  return loadStoredSession()?.user ?? null;
}

export function getCurrentSession(): AuthSession | null {
  return loadStoredSession();
}

export async function changePassword(
  _payload: ChangePasswordPayload
): Promise<void> {
  await delay(MOCK_DELAY_MS);
  const session = loadStoredSession();
  if (!session) {
    throw new Error("Session requise");
  }
}

export async function forgotPassword(
  _payload: ForgotPasswordPayload
): Promise<void> {
  await delay(MOCK_DELAY_MS);
}

export async function resetPassword(
  _payload: ResetPasswordPayload
): Promise<void> {
  await delay(MOCK_DELAY_MS);
}

export async function verifySession(): Promise<AuthSession | null> {
  const session = loadStoredSession();
  if (!session) return null;

  if (isBlockedAuthEmail(session.user.email)) {
    clearStoredSession();
    return null;
  }

  if (!resolveAuthFromEmail(session.user.email)) {
    clearStoredSession();
    return null;
  }

  if (isMockAccessToken(session.tokens.accessToken)) {
    const sessionEmail = session.user.email.trim().toLowerCase();
    if (!hasStoredPassword(sessionEmail)) {
      clearStoredSession();
      return null;
    }
  }

  const rehydratedUser = rehydrateSessionUser(session.user);
  let currentSession: AuthSession = {
    ...session,
    user: rehydratedUser,
    membership: {
      ...session.membership,
      roleIds: rehydratedUser.roles.map((r) => r.id),
    },
  };

  if (rehydratedUser.roles !== session.user.roles) {
    saveSession(currentSession);
  }

  if (isTokenExpired(currentSession.tokens.expiresAt)) {
    try {
      const tokens = await refreshToken(currentSession.tokens.refreshToken);
      currentSession = { ...currentSession, tokens };
      saveSession(currentSession);
      return currentSession;
    } catch {
      clearStoredSession();
      return null;
    }
  }

  return currentSession;
}

/** @deprecated Utiliser loadStoredSession via auth.storage */
export function loadSession(): AuthSession | null {
  return loadStoredSession();
}

/** @deprecated Utiliser clearStoredSession */
export function clearSession(): void {
  clearStoredSession();
}

/** @deprecated Utiliser saveSession */
export function persistSession(session: AuthSession): void {
  saveSession(session);
}
